import { routerActions } from "react-router-redux";
import { call, fork, join, put, select, take, takeLatest } from "redux-saga/effects";

import {
    massEcheqManageBatchDownloadRequest,
    massEcheqManageDetailRequest,
    massEcheqManageListRequest,
    massEcheqManagePreRequest,
    massEcheqManagePreviewRequest,
    massEcheqManageReadTransactionRequest,
    massEcheqManageSendRequest,
} from "middleware/echeqMassManagement";
import {
    ECHEQS_MASS_MANAGEMENT_CONFIRM_PATH,
    ECHEQS_MASS_MANAGEMENT_LIST_PATH,
} from "pages/v2/echeqMassManagement/EcheqsMassManageList";
import { buildListRequestPayload } from "pages/v2/echeqMassManagement/echeqMassManageListUtils";
import { mapBatchDetailRows } from "pages/v2/echeqMassManagement/echeqMassManageBatchDetailUtils";
import mapTransactionToBatchDetailReceipt, {
    buildEcheqActionTransactionTicketPdfData,
    getReceiptFromCacheEntry,
    getTransactionFromCacheEntry,
} from "pages/v2/echeqMassManagement/echeqMassManageBatchDetailReceiptUtils";
import {
    mapPreviewPayloadToSummary,
    mapSummaryToSendPayload,
    mapValidDetailRows,
    resolveSendActivityPath,
    DETAIL_SCOPE_BATCH,
    DETAIL_SCOPE_MODAL,
} from "pages/v2/echeqMassManagement/echeqMassManageUtils";
import { actions as formActions } from "reducers/form";
import { types } from "reducers/echeqMassManagement";
import formTypes from "reducers/types/form";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import { exportGenericTicketPdfWithSuccessMessage } from "util/transaction";
import { getResponseMessage } from "util/token";
import { INTERNAL_ERROR } from "util/responses";
import { downloadXls } from "util/download";

const sagas = [
    takeLatest(types.LIST_REQUEST, massEcheqManageList),
    takeLatest(types.PRE_REQUEST, massEcheqManagePre),
    takeLatest(types.PREVIEW_REQUEST, massEcheqManagePreview),
    takeLatest(types.SEND_REQUEST, massEcheqManageSend),
    takeLatest(types.DETAIL_REQUEST, massEcheqManageDetail),
    takeLatest(types.ITEM_RECEIPT_REQUEST, massEcheqManageItemReceipt),
    takeLatest(types.ITEM_RECEIPT_PDF_REQUEST, massEcheqManageItemReceiptPdf),
    takeLatest(types.BATCH_DOWNLOAD_REQUEST, massEcheqManageBatchDownload),
];

export default sagas;

function* massEcheqManageList({ filters, isDesktop }) {
    try {
        const response = yield call(massEcheqManageListRequest, buildListRequestPayload(filters));

        if (response.type === "W") {
            yield put({ type: types.LIST_FAILURE });
            return;
        }

        const data = response.data?.data || {};
        yield put({
            type: types.LIST_SUCCESS,
            list: data.batchList,
            pageNumber: data.pageNumber,
            totalPages: data.totalPage,
            isDesktop,
        });
    } catch (error) {
        yield put({ type: types.LIST_FAILURE });
    }
}

function* massEcheqManagePre() {
    try {
        const response = yield call(massEcheqManagePreRequest);

        if (response.type === "W" && response.data.code !== INTERNAL_ERROR) {
            const noFieldKey = response.data.data?.NO_FIELD;
            if (noFieldKey) {
                yield put(notificationActions.showNotification(i18n.get(noFieldKey, noFieldKey), "error", ["echeqs"]));
            }
            yield put({ type: types.PRE_FAILURE });

            if (noFieldKey === "echeq.massive.manage.disabled") {
                yield put(routerActions.push("/desktop"));
            } else {
                yield put(routerActions.push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
            }
            return;
        }

        const { actions } = response.data.data || {};

        if (!actions || actions.length === 0) {
            yield put({ type: types.PRE_FAILURE });
            yield put(routerActions.push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
            return;
        }

        yield put({
            type: types.PRE_SUCCESS,
            actions,
        });
    } catch (error) {
        yield put({ type: types.PRE_FAILURE });
        yield put(routerActions.push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
    }
}

function* massEcheqManagePreview({ formData }) {
    try {
        const response = yield call(massEcheqManagePreviewRequest, formData);

        if (response.type === "W" && response.data.code !== INTERNAL_ERROR) {
            const rawMessage = response.data.data?.NO_FIELD || response.data.message;
            const message = rawMessage ? i18n.get(rawMessage, rawMessage) : undefined;

            yield put(notificationActions.showNotification(message, "error", ["echeqs"]));
            yield put({
                type: types.PREVIEW_FAILURE,
            });
            return;
        }

        const summary = mapPreviewPayloadToSummary({
            ...formData,
            ...(response.data?.data || {}),
        });

        yield put({
            type: types.PREVIEW_SUCCESS,
            summary,
            formData,
        });
        yield put(routerActions.push(ECHEQS_MASS_MANAGEMENT_CONFIRM_PATH));
    } catch (error) {
        yield put({
            type: types.PREVIEW_FAILURE,
        });
    }
}

function* massEcheqManageSend({ summary, otp, setSubmitting }) {
    try {
        const activityPath = resolveSendActivityPath(summary);
        const payload = mapSummaryToSendPayload(summary, otp);
        const response = yield call(massEcheqManageSendRequest, activityPath, payload);

        if (response.type === "W" && response.data.code !== INTERNAL_ERROR) {
            const rawMessage = getResponseMessage(response) || response.data.message;
            const message = rawMessage ? i18n.get(rawMessage, rawMessage) : undefined;

            yield put(notificationActions.showNotification(message, "error", ["echeqs"], null, false));
            yield put({ type: types.SEND_FAILURE });
            return;
        }

        if (response.type === "W" && response.data.code === INTERNAL_ERROR) {
            yield put(
                notificationActions.showNotification(
                    i18n.get("echeqs.massives.manage.send.failed.notification"),
                    "error",
                    ["echeqs"],
                    null,
                    false,
                ),
            );
            yield put({ type: types.SEND_FAILURE });
            return;
        }

        yield put({ type: types.SEND_SUCCESS });

        if (response.data?.data?.idTransaction) {
            yield put(
                formActions.readTransaction({
                    pathname: `/transaction/${response.data.data.idTransaction}`,
                    newTransaction: true,
                }),
            );
            yield take("READ_TRANSACTION_REQUEST");
        }
    } catch (error) {
        const errorResponse = error?.response || error;
        const rawMessage = getResponseMessage(errorResponse) || errorResponse?.data?.message;
        const message = rawMessage ? i18n.get(rawMessage, rawMessage) : undefined;

        if (message) {
            yield put(notificationActions.showNotification(message, "error", ["echeqs"]));
        }

        yield put({ type: types.SEND_FAILURE });
    } finally {
        if (typeof setSubmitting === "function") {
            setSubmitting(false);
        }
    }
}

function* massEcheqManageDetail({ idBatch, pageNumber, isDesktop, detailScope, statusFilter }) {
    try {
        const response = yield call(massEcheqManageDetailRequest, {
            idBatch,
            pageNumber: pageNumber || 1,
            detailScope: detailScope || DETAIL_SCOPE_MODAL,
            ...(statusFilter ? { statusFilter } : {}),
        });

        if (response.type === "W") {
            yield put({ type: types.DETAIL_FAILURE });
            return;
        }

        const data = response.data?.data || {};
        const scope = detailScope || DETAIL_SCOPE_MODAL;
        const mappedDetailList =
            scope === DETAIL_SCOPE_BATCH ? mapBatchDetailRows(data.detailList) : mapValidDetailRows(data.detailList);

        yield put({
            type: types.DETAIL_SUCCESS,
            detailList: mappedDetailList,
            batchHeader: data.batch || null,
            detailScope: data.detailScope || detailScope || DETAIL_SCOPE_MODAL,
            statusFilter: statusFilter || "ALL",
            pageNumber: data.pageNumber || pageNumber || 1,
            totalPages: data.totalPage || 1,
            isDesktop,
        });
    } catch (error) {
        yield put({ type: types.DETAIL_FAILURE });
    }
}

function* massEcheqManageItemReceipt({ item }) {
    const idTransaction = item?.idTransaction;

    if (!idTransaction) {
        yield put({ type: types.ITEM_RECEIPT_FAILURE });
        return;
    }

    if (item.status === "PENDING") {
        const waitForReadTransaction = yield fork(function* waitReadTransactionOutcome() {
            return yield take([formTypes.READ_TRANSACTION_SUCCESS, formTypes.READ_TRANSACTION_FAILURE]);
        });
        yield put(
            formActions.readTransaction({
                pathname: `/transaction/${idTransaction}`,
            }),
        );
        const outcome = yield join(waitForReadTransaction);
        if (outcome?.type === formTypes.READ_TRANSACTION_FAILURE) {
            yield put({ type: types.ITEM_RECEIPT_FAILURE });
        }
        return;
    }

    const cachedEntry = yield select((state) => state.echeqMassManagement?.batchDetailReceiptCache?.[idTransaction]);
    const cachedReceipt = getReceiptFromCacheEntry(cachedEntry);

    if (cachedReceipt) {
        return;
    }

    const cachedTransaction = getTransactionFromCacheEntry(cachedEntry);
    if (cachedTransaction) {
        const receipt = mapTransactionToBatchDetailReceipt(cachedTransaction, item);

        yield put({
            type: types.ITEM_RECEIPT_SUCCESS,
            receipt,
            idTransaction,
            transaction: cachedTransaction,
        });
        return;
    }

    try {
        const response = yield call(massEcheqManageReadTransactionRequest, idTransaction);

        if (response.type === "W") {
            const rawMessage = getResponseMessage(response) || response.data?.message;
            const message = rawMessage ? i18n.get(rawMessage, rawMessage) : undefined;
            if (message) {
                yield put(notificationActions.showNotification(message, "error", ["echeqs"]));
            }
            yield put({ type: types.ITEM_RECEIPT_FAILURE });
            return;
        }

        const transaction = response?.data?.data?.transaction || {};
        const receipt = mapTransactionToBatchDetailReceipt(transaction, item);

        yield put({
            type: types.ITEM_RECEIPT_SUCCESS,
            receipt,
            idTransaction,
            transaction,
        });
    } catch (error) {
        yield put({ type: types.ITEM_RECEIPT_FAILURE });
    }
}

function* massEcheqManageItemReceiptPdf({ item, dispatch }) {
    const idTransaction = item?.idTransaction;

    if (!idTransaction || item.status !== "FINALIZADO") {
        return;
    }

    try {
        const cachedEntry = yield select(
            (state) => state.echeqMassManagement?.batchDetailReceiptCache?.[idTransaction],
        );
        let transaction = getTransactionFromCacheEntry(cachedEntry);

        if (!transaction) {
            const response = yield call(massEcheqManageReadTransactionRequest, idTransaction);

            if (response.type === "W") {
                const rawMessage = getResponseMessage(response) || response.data?.message;
                const message = rawMessage ? i18n.get(rawMessage, rawMessage) : undefined;
                if (message) {
                    yield put(notificationActions.showNotification(message, "error", ["echeqs"]));
                }
                return;
            }

            transaction = response?.data?.data?.transaction || {};

            yield put({
                type: types.UPDATE_RECEIPT_CACHE_ENTRY,
                idTransaction,
                transaction,
            });
        }

        const ticketData = buildEcheqActionTransactionTicketPdfData(transaction);
        yield call(exportGenericTicketPdfWithSuccessMessage, ticketData, dispatch, i18n.get("global.download.success"));
    } catch (error) {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["echeqs"]));
    }
}

function* massEcheqManageBatchDownload({ idBatch }) {
    if (!idBatch) {
        yield put({ type: types.BATCH_DOWNLOAD_FAILURE });
        return;
    }

    try {
        const response = yield call(massEcheqManageBatchDownloadRequest, idBatch);

        if (response.type === "W") {
            const rawMessage = getResponseMessage(response) || response.data?.message;
            const message = rawMessage ? i18n.get(rawMessage, rawMessage) : i18n.get("global.unexpectedError");
            yield put(notificationActions.showNotification(message, "error", ["echeqs"], null, false));
            yield put({ type: types.BATCH_DOWNLOAD_FAILURE });
            return;
        }

        const { fileName, content } = response.data?.data || {};
        downloadXls(fileName, content);
        yield put({ type: types.BATCH_DOWNLOAD_SUCCESS });
        yield put(
            notificationActions.showNotification(
                i18n.get("global.download.success"),
                "success",
                ["echeqs"],
                null,
                false,
            ),
        );
    } catch (error) {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["echeqs"]));
        yield put({ type: types.BATCH_DOWNLOAD_FAILURE });
    }
}
